import React, { useMemo } from 'react';

import StatCard from '../../common/StatCard/StatCard';

import usePrometheusQuery from '../../../utils/hooks/use_prometheus_query_without_range';
import { percentFormatter } from '../../../utils/formatters';
import Icon from '../../common/icons/Icon';
import COLORS from '../../../constants/colors.constants';
import GPUIcon from '../../common/icons/GPUIcon';
import { combineRangeAndOffset, unixTimestampsToPrometheusRange, valueFormatter } from '../../../utils/prometheus_utils';

const getUsedGPUQuery = (rangeVector, offset) => {
  return `avg_over_time(sum(wallaroo_kube_pod_resource_requests{resource=~"nvidia.com/gpu"})${combineRangeAndOffset(rangeVector, offset)})`;
}

const getRequestedGPUQuery = (rangeVector, offset) => {
  return `avg_over_time(sum(wallaroo_kube_pod_resource_requests{resource=~"nvidia.com/gpu"})${combineRangeAndOffset(rangeVector, offset)})`;
}

const GPUIconComponent = () => {
  return <Icon fill={COLORS.brand} icon={GPUIcon} size={24} useTailwindSizing />
}

export const useRequestedGPU = ({
  startTimestamp,
  endTimestamp,
}) => {
  const query = useMemo(() => {
    const rangeObj = unixTimestampsToPrometheusRange(startTimestamp, endTimestamp);
    return getRequestedGPUQuery(rangeObj.rangeVector, rangeObj.offset);
  }, [endTimestamp, startTimestamp])
  const prometheusResponse = usePrometheusQuery(query);

  return {
    chartData: prometheusResponse?.data,
    isLoading: prometheusResponse?.isLoading,
    message: prometheusResponse?.error || null,
  }
}

export const useUsedGPU = ({
  startTimestamp,
  endTimestamp,
}) => {
  const query = useMemo(() => {
    const rangeObj = unixTimestampsToPrometheusRange(startTimestamp, endTimestamp);
    return getUsedGPUQuery(rangeObj.rangeVector, rangeObj.offset);
  }, [endTimestamp, startTimestamp])
  const prometheusResponse = usePrometheusQuery(query);

  return {
    chartData: prometheusResponse?.data,
    isLoading: prometheusResponse?.isLoading,
    message: prometheusResponse?.error || null,
  }
}

export const ContextualTotalGPUCard = ({
  startTimestamp, endTimestamp,
}) => {
  const usedResponse = useUsedGPU({
    startTimestamp, endTimestamp
  })
  const usedData = usedResponse?.chartData;
  const usedValue = usedData?.length ? usedData[1] : 0;

  const requestedResponse = useRequestedGPU({
    startTimestamp, endTimestamp
  })
  const requestedData = requestedResponse?.chartData;
  const requestedValue = requestedData?.length ? requestedData[1] : 0;

  const subtitle = `Used ${valueFormatter.format(usedValue)}/${valueFormatter.format(requestedValue)} GPUs Requested`;

  const content = requestedValue ? percentFormatter.format(usedValue / requestedValue) : 0;
  const isLoading = requestedResponse?.isLoading || usedResponse?.isLoading;

  return (
    <StatCard
      className=""
      icon={GPUIconComponent}
      header="Average GPU Usage Over Time"
      subtitle={subtitle}
      content={`${content}`}
      isLoading={isLoading}
    />
  )
}

export default ContextualTotalGPUCard;
